"use client";

import { useEffect } from "react";

/**
 * Last-resort UI for the events routes' error boundaries. Intentionally
 * self-contained — only React + Tailwind, no data fetching or heavy imports —
 * so the fallback itself can never throw. Shown only when something unexpected
 * crashes during render; ordinary "no data / fetch failed" cases are already
 * handled inside the pages with the friendly NoWebinars empty state.
 */
export default function EventsErrorFallback({
  error,
  reset,
}: {
  error?: Error & { digest?: string };
  reset?: () => void;
}) {
  useEffect(() => {
    if (error) console.error("Events route error boundary:", error);
  }, [error]);

  return (
    <main className="w-full min-h-screen bg-[#000000] flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-[#12203A] flex items-center justify-center mb-6">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect
            x="3"
            y="4.5"
            width="18"
            height="16"
            rx="3"
            stroke="#3399FF"
            strokeWidth="1.6"
          />
          <path d="M3 9h18" stroke="#3399FF" strokeWidth="1.6" />
          <path
            d="M8 2.5v4M16 2.5v4"
            stroke="#3399FF"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            d="m9.5 14.5 1.8 1.8 3.2-3.4"
            stroke="#FFBA08"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h1 className="text-white font-semibold text-[24px] sm:text-[28px] mb-3">
        We couldn&apos;t load this page
      </h1>
      <p className="text-[#9AA6B2] text-[15px] max-w-[420px] mb-7">
        Something went wrong on our end. Please try again in a moment.
      </p>

      <div className="flex items-center gap-3">
        {reset && (
          <button
            type="button"
            onClick={() => reset()}
            className="bg-[#3399FF] text-white font-semibold rounded-full px-6 py-2.5 hover:brightness-95 transition"
          >
            Try again
          </button>
        )}
        <a
          href="/events"
          className="text-[#3399FF] font-semibold rounded-full px-6 py-2.5 border border-[#3399FF]/40 hover:bg-[#3399FF]/10 transition"
        >
          Back to events
        </a>
      </div>
    </main>
  );
}
